import { evidenceDefinitionContract } from "@clipboard-health/contract-documents-service";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { z } from "zod";

import { EVIDENCE_DEFINITIONS_QUERY_KEY } from "../constants";
import { type EvidenceDefinition } from "../types";
import { extractCursorFromUrl } from "../utils";

const queryParamsSchema = z.object({
  page: z.object({
    size: z.number(),
    cursor: z.string().optional(),
  }),
});

const responseSchema = evidenceDefinitionContract.list.responses[200];

const PAGE_SIZE = 100;

function getUrl() {
  return `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/v2/evidence-definitions`;
}

export function useGetAllEvidenceDefinitions() {
  const url = getUrl();

  const query = useInfiniteQuery({
    queryKey: [EVIDENCE_DEFINITIONS_QUERY_KEY],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const response = await get({
        url,
        queryParams: {
          page: {
            size: PAGE_SIZE,
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        },
        queryParamsSchema,
        responseSchema,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.links.next) {
        return undefined;
      }

      return extractCursorFromUrl(lastPage.links.next);
    },
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_EVIDENCE_DEFINITIONS_FAILURE },
  });

  const { hasNextPage, isFetching, fetchNextPage, isError } = query;

  useEffect(() => {
    if (hasNextPage && !isFetching && !isError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetching, isError, fetchNextPage]);

  const definitions: EvidenceDefinition[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  );

  return {
    definitions,
    isLoading: query.isLoading || (query.isFetching && Boolean(hasNextPage)),
    isError: query.isError,
  };
}
